import type { AmhsFinalSimulationLeg, BrowserKineticVehicleLeg } from '../types/simulationPlayback';
import type { OhtTimelineEvent } from '../types/amhsStream';
import { railIdFromSegmentId } from './layoutElementDisplayName';
import {
  activeSegmentWindow,
  indexApiLegsByOht,
  isTravelPhase,
  legPathSegmentIds,
} from './simulationPhaseUi';

export type FloorBounds = {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
};

export type TravelPathLine = {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  partial: boolean;
};

const MIN_SPAN = 80;

function mergeIntervalDuration(intervals: Array<[number, number]>): number {
  if (intervals.length === 0) return 0;
  intervals.sort((a, b) => a[0] - b[0]);
  let total = 0;
  let [curStart, curEnd] = intervals[0]!;
  for (let i = 1; i < intervals.length; i++) {
    const [start, end] = intervals[i]!;
    if (start <= curEnd) {
      curEnd = Math.max(curEnd, end);
    } else {
      total += curEnd - curStart;
      curStart = start;
      curEnd = end;
    }
  }
  total += curEnd - curStart;
  return total;
}

/** Fit bounds around geometry with padding — avoids shrinking tracks inside a huge DXF floor. */
export function boundsFromPoints(
  points: Array<{ x: number; y: number }>,
  paddingRatio = 0.12
): FloorBounds | null {
  if (points.length === 0) return null;

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const { x, y } of points) {
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  }

  if (!Number.isFinite(minX)) return null;

  const spanX = Math.max(maxX - minX, MIN_SPAN);
  const spanY = Math.max(maxY - minY, MIN_SPAN);
  const padX = spanX * paddingRatio;
  const padY = spanY * paddingRatio;

  return {
    minX: minX - padX,
    minY: minY - padY,
    maxX: maxX + padX,
    maxY: maxY + padY,
  };
}

export function normalizeTraveledSegmentIds(ids: Iterable<string>): Set<string> {
  const normalized = new Set<string>();
  for (const id of ids) {
    normalized.add(id);
    normalized.add(railIdFromSegmentId(id));
  }
  return normalized;
}

export function segmentIsTraveled(segmentId: string, traveled: Set<string>): boolean {
  if (traveled.has(segmentId)) return true;
  const railId = railIdFromSegmentId(segmentId);
  return traveled.has(railId);
}

/** Busy seconds for one backend OHT id up to `simTime`. */
export function computeOhtBusySec(
  ohtId: string,
  apiLegs: AmhsFinalSimulationLeg[],
  simTime: number
): number {
  if (simTime <= 0) return 0;
  const legs = indexApiLegsByOht(apiLegs).get(ohtId) ?? [];
  const intervals: Array<[number, number]> = [];

  for (const leg of legs) {
    if (leg.startSec >= simTime) continue;
    const start = Math.max(0, leg.startSec);
    const end = Math.min(simTime, leg.endSec);
    if (end <= start) continue;
    if (isTravelPhase(leg) || leg.phase === 'process_wait') {
      intervals.push([start, end]);
    }
  }

  return mergeIntervalDuration(intervals);
}

/** Utilization % for a layout OHT (tries every id alias the backend might use). */
export function computeOhtUtilizationPct(
  ohtIds: string[],
  apiLegs: AmhsFinalSimulationLeg[],
  simTime: number
): number | null {
  if (simTime <= 0 || ohtIds.length === 0) return null;
  let busySec = 0;
  for (const ohtId of ohtIds) {
    busySec = Math.max(busySec, computeOhtBusySec(ohtId, apiLegs, simTime));
  }
  if (busySec <= 0) return 0;
  return Math.min(100, (busySec / simTime) * 100);
}

export function formatUtilizationPct(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return `${value.toFixed(1)}%`;
}

/** Segment ids this OHT has traversed up to `simTime`. */
export function collectOhtTraveledSegmentIds(
  ohtIds: string[],
  apiLegs: AmhsFinalSimulationLeg[],
  simTime: number
): Set<string> {
  const traveled = new Set<string>();

  for (const ohtId of ohtIds) {
    const legs = indexApiLegsByOht(apiLegs).get(ohtId) ?? [];
    for (const leg of legs) {
      if (leg.startSec > simTime) continue;
      const pathSegs = legPathSegmentIds(leg);
      const wins = leg.segmentWindows;

      if (wins && wins.length > 0) {
        for (const win of wins) {
          if (win.startS >= simTime) continue;
          traveled.add(win.segmentId);
        }
        continue;
      }

      if (leg.endSec <= simTime) {
        for (const segmentId of pathSegs) traveled.add(segmentId);
        continue;
      }

      const active = activeSegmentWindow(leg, simTime);
      if (!active) continue;
      const activeIdx = pathSegs.indexOf(active.segmentId);
      const limit = activeIdx >= 0 ? activeIdx + 1 : pathSegs.length;
      for (let i = 0; i < limit; i++) traveled.add(pathSegs[i]!);
    }
  }

  return traveled;
}

/** World-space leg chords for the history map (works with live-stream coords). */
export function collectOhtTravelPathLines(
  ohtIds: string[],
  legsByVehicle: Record<string, BrowserKineticVehicleLeg[]>,
  simTime: number
): TravelPathLine[] {
  const lines: TravelPathLine[] = [];

  for (const ohtId of ohtIds) {
    const legs = legsByVehicle[ohtId] ?? [];
    for (const leg of legs) {
      if (leg.startS >= simTime) continue;
      if (
        !Number.isFinite(leg.fromX) ||
        !Number.isFinite(leg.fromY) ||
        !Number.isFinite(leg.toX) ||
        !Number.isFinite(leg.toY)
      ) {
        continue;
      }
      lines.push({
        x1: leg.fromX,
        y1: leg.fromY,
        x2: leg.toX,
        y2: leg.toY,
        partial: leg.endS > simTime,
      });
    }
  }

  return lines;
}

export type OhtHistoryRow = {
  startS: number;
  endS: number;
  label: string;
  kind: 'travel' | 'wait' | 'transfer' | 'other';
};

function timelineKind(event: OhtTimelineEvent): OhtHistoryRow['kind'] {
  if (event.type === 'vehicle_leg') return event.stationary ? 'wait' : 'travel';
  if (event.type === 'junction_wait') return 'wait';
  if (event.type === 'transfer_event') return 'transfer';
  return 'other';
}

function timelineLabel(event: OhtTimelineEvent): string {
  if (event.type === 'vehicle_leg') {
    const from = event.leg.fromId || '—';
    const to = event.leg.toId || '—';
    return event.stationary ? `Wait @ ${to}` : `${from} → ${to}`;
  }
  if (event.type === 'junction_wait') {
    return event.reason === 'junctionDwell' ? 'Junction dwell' : 'Junction wait';
  }
  if (event.type === 'transfer_event') {
    const location =
      event.frame.portId || event.frame.stockerId || event.frame.toolId || 'port';
    return `${event.action} @ ${location}`;
  }
  return 'event';
}

/** Past timeline rows for the history tab (newest last, capped). */
export function buildOhtHistoryRows(
  ohtIds: string[],
  ohtTimelines: Record<string, OhtTimelineEvent[]>,
  simTime: number,
  maxRows = 12
): OhtHistoryRow[] {
  const merged: OhtHistoryRow[] = [];

  for (const ohtId of ohtIds) {
    const events = ohtTimelines[ohtId] ?? [];
    for (const event of events) {
      if (event.startS >= simTime) continue;
      merged.push({
        startS: event.startS,
        endS: Math.min(simTime, event.endS),
        label: timelineLabel(event),
        kind: timelineKind(event),
      });
    }
  }

  merged.sort((a, b) => a.startS - b.startS || a.endS - b.endS);
  if (merged.length <= maxRows) return merged;
  return merged.slice(-maxRows);
}
